<script lang="ts" setup>
function colClass(i: number) {
  if (i === 1) return 'col-number';
  if (i === 2) return 'col-description';
  if (i === 3) return 'col-date';
  return 'col-amount';
}

function cellStyle(col: number): Record<string, string> {
  if (col === 1) return { width: '2.5rem' };
  if (col === 2) return { width: '60%' };
  if (col === 3) return { width: '4.5rem' };
  return { width: '4rem', marginLeft: 'auto' };
}
</script>
<template>
  <div class="card skeleton-card">
    <!-- Card header -->
    <div class="card-header skeleton-header">
      <div class="card-header-title">
        <div class="skeleton-line skeleton-title" />
      </div>
    </div>
    <div class="summary-bar">
      <div class="eq-table">
        <div class="eq-row">
          <div class="skeleton-line skeleton-label" />
          <div class="skeleton-line skeleton-amount" />
        </div>
        <div class="eq-row">
          <div class="skeleton-line skeleton-label" />
          <div class="skeleton-line skeleton-amount" />
        </div>
        <div class="eq-row">
          <div class="skeleton-line skeleton-label skeleton-label--short" />
          <div class="skeleton-line skeleton-pct" />
        </div>
        <div class="eq-divider" />
        <div class="eq-row">
          <div class="skeleton-line skeleton-label skeleton-label--bold" />
          <div class="skeleton-line skeleton-amount skeleton-amount--result" />
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="skeleton-table-wrap">
      <table class="table is-fullwidth mb-0">
        <thead>
          <tr>
            <th v-for="i in 6" :key="i" :class="['col-th', colClass(i)]">
              <div class="skeleton-line skeleton-th" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in 4" :key="row">
            <td v-for="col in 6" :key="col" :class="['col-td', colClass(col)]">
              <div class="skeleton-line" :style="cellStyle(col)" />
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="totals-row">
            <td colspan="3" />
            <td><div class="skeleton-line skeleton-total" /></td>
            <td><div class="skeleton-line skeleton-total" /></td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<style scoped>
@keyframes shimmer {
  0% {
    background-position: -400px 0;
  }
  100% {
    background-position: 400px 0;
  }
}

.skeleton-line {
  border-radius: 4px;
  background: linear-gradient(90deg, #ebebeb 25%, #f5f5f5 50%, #ebebeb 75%);
  background-size: 800px 100%;
  animation: shimmer 1.4s infinite linear;
  height: 0.85rem;
}

/* ---- Card header ---- */
.skeleton-card {
  overflow: hidden;
}

.skeleton-card .card-header {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.skeleton-title {
  width: 10rem;
}

/* ---- Summary bar ---- */
.summary-bar {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fafafc;
}

.eq-table {
  display: inline-grid;
  grid-template-columns: auto auto;
  gap: 0;
}

.eq-row {
  display: contents;
}

.eq-row > * {
  margin: 0.35rem 0;
}

.eq-row > *:last-child {
  margin-left: 5rem;
}

.eq-divider {
  grid-column: 1 / -1;
  border-top: 1px solid #d0d0d6;
  margin: 0.3rem 0;
}

.skeleton-label {
  width: 9rem;
  align-self: center;
}

.skeleton-label--short {
  width: 6rem;
}

.skeleton-label--bold {
  width: 8rem;
}

.skeleton-amount {
  width: 8rem;
}

.skeleton-amount--result {
  width: 8rem;
  height: 1rem;
}

.skeleton-pct {
  width: 4rem;
}

/* ---- Table ---- */
.skeleton-table-wrap {
  background-color: #fff;
}

.skeleton-th {
  height: 0.75rem;
  width: 80%;
}

.col-number {
  width: 4rem;
}
.col-description {
  width: 40%;
}
.col-date {
  width: 7rem;
}
.col-amount {
  text-align: right;
}

.col-td {
  vertical-align: middle;
  padding-top: 0.55rem;
  padding-bottom: 0.55rem;
}

.totals-row td {
  padding-top: 0.6rem;
  padding-bottom: 0.6rem;
  border-top: 2px solid #e8e8e8;
}

.skeleton-total {
  width: 5rem;
  margin-left: auto;
}
</style>
